import { toast } from "sonner";

/**
 * "Get it on Google Play" badge button.
 * Renders the official Google Play badge style.
 *
 * Behaviour:
 *  - If `url` prop (or PLAYSTORE_URL) is set → opens the Play Store link in a new tab.
 *  - If empty → shows a friendly "Coming Soon" toast.
 *
 * To go live: set the URL via Admin → Site Content → Play Store URL,
 * or hardcode it in PLAYSTORE_URL below.
 */
const PLAYSTORE_URL = ""; // e.g. "https://play.google.com/store/apps/details?id=com.shaktiwings"

export default function PlayStoreButton({ url, className = "", testId = "playstore-btn" }) {
  const link = url || PLAYSTORE_URL;

  const handleClick = (e) => {
    if (!link) {
      e.preventDefault();
      toast.info("Android app coming soon — stay tuned! 🚀");
    }
  };

  const Tag = link ? "a" : "button";
  const props = link
    ? { href: link, target: "_blank", rel: "noopener noreferrer" }
    : { type: "button" };

  return (
    <Tag
      {...props}
      onClick={handleClick}
      data-testid={testId}
      className={`inline-flex items-center gap-3 px-5 py-2.5 rounded-xl bg-black hover:bg-neutral-800 text-white transition-colors shadow-lg ${className}`}
      aria-label="Get it on Google Play"
    >
      {/* Google Play triangle logo */}
      <svg viewBox="0 0 512 512" className="w-7 h-7" aria-hidden="true">
        <linearGradient id="gp-grad-1" x1="183.61" y1="382.39" x2="297.04" y2="195.97" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#00a0ff" />
          <stop offset="0.007" stopColor="#00a1ff" />
          <stop offset="0.26" stopColor="#00beff" />
          <stop offset="0.512" stopColor="#00d2ff" />
          <stop offset="0.76" stopColor="#00dfff" />
          <stop offset="1" stopColor="#00e3ff" />
        </linearGradient>
        <linearGradient id="gp-grad-2" x1="416.49" y1="254.93" x2="172.92" y2="254.93" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffe000" />
          <stop offset="0.409" stopColor="#ffbd00" />
          <stop offset="0.775" stopColor="#ffa500" />
          <stop offset="1" stopColor="#ff9c00" />
        </linearGradient>
        <linearGradient id="gp-grad-3" x1="320.13" y1="232.07" x2="65.4" y2="-22.65" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ff3a44" />
          <stop offset="1" stopColor="#c31162" />
        </linearGradient>
        <linearGradient id="gp-grad-4" x1="170.49" y1="495.7" x2="284.21" y2="381.98" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#32a071" />
          <stop offset="0.069" stopColor="#2da771" />
          <stop offset="0.476" stopColor="#15cf74" />
          <stop offset="0.801" stopColor="#06e775" />
          <stop offset="1" stopColor="#00f076" />
        </linearGradient>
        <path d="M105.4 99.3c-7.2 7.6-11.5 19.5-11.5 34.9v243.7c0 15.4 4.3 27.3 11.5 34.9l.8.8 136.5-136.5v-3.2L106.2 98.5l-.8.8z" fill="url(#gp-grad-1)" />
        <path d="M288 322.4l-45.5-45.5v-3.2L288 228.2l1 .6 53.9 30.6c15.4 8.7 15.4 23 0 31.8L289 321.8l-1 .6z" fill="url(#gp-grad-2)" />
        <path d="M289 321.8l-46.5-46.5L105.4 412.4c5.1 5.4 13.5 6 22.9.7L289 321.8" fill="url(#gp-grad-4)" />
        <path d="M289 228.8L128.3 137.6c-9.4-5.3-17.8-4.7-22.9.7l137.1 137.1L289 228.8z" fill="url(#gp-grad-3)" />
      </svg>
      <div className="flex flex-col items-start leading-tight text-left">
        <span className="text-[10px] font-normal opacity-90">GET IT ON</span>
        <span className="text-base font-semibold tracking-tight">Google Play</span>
      </div>
    </Tag>
  );
}
